import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { createInvoice } from '../../../data/api/incomes/InvoiceRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.16)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a', borderWidth: '2px' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(15, 34, 77, 0.65)', fontWeight: 700 },
};

const INVOICE_TYPES = ['FACTURA', 'NOTA_CREDITO', 'NOTA_DEBITO', 'NO_FACTURA', 'PRUEBA'];
// Razón de negocio (invoice_origin). El admin elige al crear manualmente.
const INVOICE_ORIGINS = ['Bingo', 'Compra', 'Donacion', 'Contrato'];
// Sistema que originó la transacción (transaction_origin). Para facturas
// manuales siempre es FENABOCOL — WOMPI/PSE son automáticas.
const MANUAL_TRANSACTION_ORIGIN = 'FENABOCOL';
// Métodos de pago concretos (transaction_type) que aplican a una factura
// manual sin pasarela web.
const PAYMENT_METHODS = [
    { value: 'NEQUI', label: 'Nequi' },
    { value: 'DAVIPLATA', label: 'Daviplata' },
    { value: 'EFECTIVO', label: 'Efectivo' },
    { value: 'TRANSFERENCIA', label: 'Transferencia' },
    { value: 'MERCADO_PAGO', label: 'Mercado Pago' },
];

const fmtCOP = (cop) => Number(cop || 0).toLocaleString('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0,
});

const genReference = () => {
    const ts = Date.now().toString(36).toUpperCase();
    const rnd = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `MAN-${ts}-${rnd}`;
};

/**
 * Bloquea negativos, notación científica y, lo más importante, evita que
 * la rueda del mouse cambie el valor numérico cuando el input tiene foco.
 * Por defecto los <input type="number"> en navegadores modernos sí lo
 * cambian — basta pasar por encima haciendo scroll para alterar montos.
 */
const numberFieldProps = (extra = {}) => ({
    type: 'number',
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
        if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
    },
    inputProps: { min: 0, step: 1, ...extra },
});

const CreateInvoiceModal = ({ open, onClose, onCreated }) => {
    const { token } = useIndex();
    const [submitting, setSubmitting] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            payment_reference: '',
            description: '',
            invoice_type: 'FACTURA',
            invoice_origin: 'Compra',
            transaction_type: 'EFECTIVO',
            currency: 'COP',
            discount_cop: 0,
            taxes_cop: 0,
            expiration_time: '',
            redirect_url: '',
            user_id: '',
            approved: false,
            lines: [{ product_name: '', description: '', amount_cop: 0 }],
        },
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'lines' });

    useEffect(() => {
        if (open) {
            reset({
                payment_reference: genReference(),
                description: '',
                invoice_type: 'FACTURA',
                invoice_origin: 'Compra',
                transaction_type: 'EFECTIVO',
                currency: 'COP',
                discount_cop: 0,
                taxes_cop: 0,
                expiration_time: '',
                redirect_url: '',
                user_id: '',
                approved: false,
                lines: [{ product_name: '', description: '', amount_cop: 0 }],
            });
        }
    }, [open, reset]);

    const watchLines = watch('lines');
    const watchDiscount = Number(watch('discount_cop') || 0);
    const watchTaxes = Number(watch('taxes_cop') || 0);

    const subtotalCOP = (watchLines || []).reduce(
        (acc, l) => acc + Number(l?.amount_cop || 0),
        0,
    );
    const totalCOP = Math.max(0, subtotalCOP - watchDiscount + watchTaxes);

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose?.();
    };

    const onSubmit = async (form) => {
        // Defensa extra: por más que los inputs ya bloquean negativos,
        // un usuario podría pegarlos manualmente.
        const hasNegative = (watchLines || []).some((l) => Number(l?.amount_cop) < 0)
            || watchDiscount < 0
            || watchTaxes < 0;
        if (hasNegative) {
            Report.warning('Atención', 'Los montos no pueden ser negativos.', 'Okay');
            return;
        }

        if (subtotalCOP <= 0) {
            Report.warning('Atención', 'Debes agregar al menos una línea con monto mayor a 0.', 'Okay');
            return;
        }

        if (totalCOP < 1) {
            Report.warning('Atención', 'El total de la factura debe ser de al menos $1 COP.', 'Okay');
            return;
        }

        // Convertimos COP -> centavos antes de enviar (la BD guarda en centavos).
        const payload = {
            payment_reference: form.payment_reference,
            description: form.description,
            amount: subtotalCOP * 100,
            discount: watchDiscount * 100,
            taxes: watchTaxes * 100,
            total: totalCOP * 100,
            currency: form.currency || 'COP',
            invoice_type: form.invoice_type,
            // razón de negocio elegida por el admin
            invoice_origin: form.invoice_origin,
            // sistema que originó la transacción: las facturas manuales
            // siempre se marcan como FENABOCOL.
            transaction_origin: MANUAL_TRANSACTION_ORIGIN,
            // método de pago concreto elegido (Nequi, Efectivo, ...)
            transaction_type: form.transaction_type,
            // Estado: por defecto PENDING; si el admin marcó "aprobada" al
            // crearla la dejamos directamente como APPROVED.
            transaction_response: form.approved ? 'APPROVED' : 'PENDING',
            expiration_time: form.expiration_time || null,
            redirect_url: form.redirect_url || null,
            status: 1,
            user_id: form.user_id ? Number(form.user_id) : null,
            lines: (form.lines || [])
                .filter((l) => l.product_name && Number(l.amount_cop) > 0)
                .map((l) => ({
                    product_name: l.product_name,
                    description: l.description || l.product_name,
                    amount: Number(l.amount_cop) * 100,
                })),
        };

        setSubmitting(true);
        try {
            const { data } = await createInvoice(token, payload);
            if (data?.success) {
                Report.success('Éxito', data?.message ?? 'Factura creada correctamente.', 'Okay', { textAlign: 'center' });
                onCreated?.();
                onClose?.();
                reset();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <ModalCard>
                    <ModalHeader>
                        <Box className="title-wrap">
                            <Box className="icon-box">
                                <ReceiptLongOutlinedIcon />
                            </Box>
                            <Box>
                                <Typography className="title">Crear factura</Typography>
                                <Typography className="subtitle">
                                    Registra manualmente una factura con sus líneas.
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </ModalHeader>

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <SectionCard>
                            <SectionTitle>Información general</SectionTitle>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Referencia de pago *"
                                        sx={fieldStyles}
                                        error={!!errors.payment_reference}
                                        helperText={errors.payment_reference?.message}
                                        {...register('payment_reference', {
                                            required: 'La referencia es obligatoria.',
                                            maxLength: { value: 36, message: 'Máximo 36 caracteres.' },
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Moneda *"
                                        sx={fieldStyles}
                                        {...register('currency', { required: true })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Descripción *"
                                        multiline
                                        minRows={2}
                                        sx={fieldStyles}
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        {...register('description', {
                                            required: 'La descripción es obligatoria.',
                                        })}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="invoice_type"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth size="small" sx={fieldStyles}>
                                                <InputLabel>Tipo *</InputLabel>
                                                <Select {...field} label="Tipo *">
                                                    {INVOICE_TYPES.map((t) => (
                                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="invoice_origin"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth size="small" sx={fieldStyles}>
                                                <InputLabel>Origen *</InputLabel>
                                                <Select {...field} label="Origen *">
                                                    {INVOICE_ORIGINS.map((o) => (
                                                        <MenuItem key={o} value={o}>{o}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="transaction_type"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth size="small" sx={fieldStyles}>
                                                <InputLabel>Medio de pago *</InputLabel>
                                                <Select {...field} label="Medio de pago *">
                                                    {PAYMENT_METHODS.map((o) => (
                                                        <MenuItem key={o.value} value={o.value}>
                                                            {o.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="datetime-local"
                                        label="Expiración"
                                        InputLabelProps={{ shrink: true }}
                                        sx={fieldStyles}
                                        {...register('expiration_time')}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="ID usuario (opcional)"
                                        sx={fieldStyles}
                                        {...numberFieldProps()}
                                        {...register('user_id')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="URL de redirección (opcional)"
                                        sx={fieldStyles}
                                        {...register('redirect_url')}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="approved"
                                        control={control}
                                        render={({ field }) => (
                                            <ApprovedToggle approved={field.value}>
                                                <Box className="info">
                                                    <Typography className="title">
                                                        {field.value ? 'Factura aprobada' : 'Pendiente de aprobación'}
                                                    </Typography>
                                                    <Typography className="subtitle">
                                                        {field.value
                                                            ? 'Se creará con estado APPROVED.'
                                                            : 'Podrás aprobarla después desde el listado.'}
                                                    </Typography>
                                                </Box>
                                                <FormControlLabel
                                                    sx={{ m: 0 }}
                                                    control={
                                                        <Switch
                                                            checked={!!field.value}
                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                        />
                                                    }
                                                    label=""
                                                />
                                            </ApprovedToggle>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </SectionCard>

                        <SectionCard>
                            <SectionTitleRow>
                                <SectionTitle as="span">Líneas de la factura</SectionTitle>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => append({ product_name: '', description: '', amount_cop: 0 })}
                                >
                                    Agregar línea
                                </Button>
                            </SectionTitleRow>

                            {fields.map((field, idx) => (
                                <LineRow key={field.id}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Producto *"
                                                sx={fieldStyles}
                                                {...register(`lines.${idx}.product_name`, {
                                                    required: 'Producto requerido.',
                                                })}
                                                error={!!errors?.lines?.[idx]?.product_name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Descripción *"
                                                sx={fieldStyles}
                                                {...register(`lines.${idx}.description`, {
                                                    required: 'Descripción requerida.',
                                                })}
                                                error={!!errors?.lines?.[idx]?.description}
                                            />
                                        </Grid>
                                        <Grid item xs={10} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Monto (COP) *"
                                                sx={fieldStyles}
                                                {...numberFieldProps({ step: 1000 })}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                }}
                                                {...register(`lines.${idx}.amount_cop`, {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: 'No negativo.' },
                                                })}
                                                error={!!errors?.lines?.[idx]?.amount_cop}
                                            />
                                        </Grid>
                                        <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => fields.length > 1 && remove(idx)}
                                                disabled={fields.length <= 1}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </LineRow>
                            ))}
                        </SectionCard>

                        <SectionCard>
                            <SectionTitle>Totales</SectionTitle>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Descuento (COP)"
                                        sx={fieldStyles}
                                        {...numberFieldProps({ step: 1000 })}
                                        {...register('discount_cop', {
                                            valueAsNumber: true,
                                            min: { value: 0, message: 'No negativo.' },
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Impuestos (COP)"
                                        sx={fieldStyles}
                                        {...numberFieldProps({ step: 1000 })}
                                        {...register('taxes_cop', {
                                            valueAsNumber: true,
                                            min: { value: 0, message: 'No negativo.' },
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Summary>
                                        <SummaryRow>
                                            <span>Subtotal</span>
                                            <span>{fmtCOP(subtotalCOP)}</span>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <span>Descuento</span>
                                            <span>- {fmtCOP(watchDiscount)}</span>
                                        </SummaryRow>
                                        <SummaryRow>
                                            <span>Impuestos</span>
                                            <span>+ {fmtCOP(watchTaxes)}</span>
                                        </SummaryRow>
                                        <SummaryRow className="total">
                                            <span>Total</span>
                                            <span>{fmtCOP(totalCOP)}</span>
                                        </SummaryRow>
                                    </Summary>
                                </Grid>
                            </Grid>
                        </SectionCard>

                        <Actions>
                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                startIcon={<ReceiptLongOutlinedIcon />}
                                sx={{
                                    borderRadius: '14px',
                                    py: 1.1,
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>Crear factura</span>
                            </LoadingButton>
                        </Actions>
                    </form>
                </ModalCard>
            </Center>
        </Modal>
    );
};

export default CreateInvoiceModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(94vw, 880px);
    max-height: 92vh;
    outline: none;
`;

const ModalCard = styled.div`
    width: 100%;
    max-height: 92vh;
    overflow-y: auto;
    border-radius: 22px;
    padding: 1.2rem;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.28);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22);

    @media (max-width: 600px) {
        padding: 0.9rem;
        border-radius: 18px;
    }
`;

const ModalHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.82rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 16px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);
`;

const SectionTitle = styled.div`
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-size: 0.85rem;
    margin-bottom: 0.8rem;
`;

const SectionTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.6rem;
`;

const LineRow = styled.div`
    padding: 0.5rem 0.5rem;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 0.4rem;
    border: 1px solid rgba(15, 34, 77, 0.06);
`;

const Summary = styled.div`
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.08);
    border-radius: 12px;
    padding: 0.7rem 0.9rem;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    color: rgba(15, 34, 77, 0.78);
    font-size: 0.86rem;
    padding: 0.15rem 0;

    &.total {
        margin-top: 0.3rem;
        padding-top: 0.4rem;
        border-top: 1px solid rgba(15, 34, 77, 0.08);
        font-weight: 900;
        color: #10285f;
        font-size: 1rem;
    }
`;

const Actions = styled.div`
    margin-top: 0.3rem;
`;

const ApprovedToggle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.7rem;
    padding: 0.7rem 0.9rem;
    border-radius: 14px;
    border: 1px solid ${(p) => (p.approved ? 'rgba(46, 125, 50, 0.4)' : 'rgba(15, 34, 77, 0.12)')};
    background: ${(p) => (p.approved ? 'rgba(76, 175, 80, 0.10)' : '#fff')};
    transition: 0.2s ease;

    .info { min-width: 0; }

    .title {
        color: ${(p) => (p.approved ? '#2e7d32' : '#10285f')};
        font-weight: 900;
        font-size: 0.95rem;
        line-height: 1.1;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.78rem;
        font-weight: 600;
        margin-top: 0.15rem;
    }
`;
